/* eslint-disable indent */
import { ReleaseNoteByod } from '@src/main/modules/parameters/domain/release-notes-byod/release-note-byod.interface';
import ReleaseNoteByodForm from '@src/main/modules/parameters/ui/release-notes-byod/components/ReleaseNoteByodForm';
import { getReleaseNoteByodSchema } from '@src/main/modules/parameters/ui/release-notes-byod/components/releaseNoteByodSchema';
import { useCreateReleaseNoteByod } from '@src/main/modules/parameters/ui/release-notes-byod/hooks/create/useCreateReleaseNoteByod';
import { useDeleteReleaseNoteByod } from '@src/main/modules/parameters/ui/release-notes-byod/hooks/delete/useDeleteReleaseNoteByod';
import { useReleaseNotesByod } from '@src/main/modules/parameters/ui/release-notes-byod/hooks/find/useReleaseNotesByod';
import { useUpdateReleaseNoteByod } from '@src/main/modules/parameters/ui/release-notes-byod/hooks/update/useUpdateReleaseNoteByod';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTableSingle from '@src/main/shared/ui/components/table/AppTableSingle';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

interface TableRow extends ReleaseNoteByod {
  id: string | number;
}

const ReleaseNotesByod: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: releaseNotesByodTable = [], isLoading: loadingReleaseNotesByod } =
    useReleaseNotesByod();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditReleaseNoteByod, setNewEditReleaseNoteByod] = useState<ReleaseNoteByod>({
    releaseNoteByodId: undefined,
    description: '',
    version: '',
  });
  const [selectedReleaseNotesByod, setSelectedReleaseNotesByod] = useState<TableRow[]>([]);
  const [releaseNoteByodToDelete, setReleaseNoteByodToDelete] = useState<TableRow | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasEditPermissions = hasPermission(PERMISSIONS.PARAMETERS.RELEASE_NOTES_BYOD.edit);

  const toolbar = [
    false,
    hasEditPermissions,
    selectedReleaseNotesByod.length > 0,
    true,
    false,
    false,
  ];

  const { mutateAsync: createReleaseNoteByod } = useCreateReleaseNoteByod();
  const { mutateAsync: updateReleaseNoteByod } = useUpdateReleaseNoteByod();
  const { mutateAsync: deleteReleaseNoteByod } = useDeleteReleaseNoteByod();

  const releaseNotesByodWithId: TableRow[] = releaseNotesByodTable.map((s) => ({
    id: s.releaseNoteByodId!,
    ...s,
  }));

  const handleShowCreateReleaseNoteByod = () => {
    setNewEditReleaseNoteByod({
      releaseNoteByodId: undefined,
      description: '',
      version: '',
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleEditReleaseNoteByod = (releaseNoteByod: TableRow) => {
    setNewEditReleaseNoteByod({ ...releaseNoteByod });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    const schema = getReleaseNoteByodSchema(t);

    try {
      await schema.parseAsync(newEditReleaseNoteByod);

      if (newEditReleaseNoteByod.releaseNoteByodId) {
        await updateReleaseNoteByod({
          id: String(newEditReleaseNoteByod.releaseNoteByodId),
          data: {
            description: newEditReleaseNoteByod.description,
          },
        });
        showSuccessMessage(toast, t('parameters.releaseNotesByod.updatedSuccess'));
      } else {
        await createReleaseNoteByod({
          version: newEditReleaseNoteByod.version,
          description: newEditReleaseNoteByod.description,
        });
        showSuccessMessage(toast, t('parameters.releaseNotesByod.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['release-notes-byod'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0] as string;
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleDeleteReleaseNoteByod = (releaseNoteByod: TableRow) => {
    setReleaseNoteByodToDelete(releaseNoteByod);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        await Promise.all(
          selectedReleaseNotesByod.map((s) => deleteReleaseNoteByod(s.releaseNoteByodId!)),
        );
        setSelectedReleaseNotesByod([]);
      } else if (releaseNoteByodToDelete) {
        await deleteReleaseNoteByod(releaseNoteByodToDelete.releaseNoteByodId!);
        setSelectedReleaseNotesByod((prev) =>
          prev.filter((s) => s.releaseNoteByodId !== releaseNoteByodToDelete.releaseNoteByodId),
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['release-notes-byod'] });
      showSuccessMessage(toast, t('parameters.releaseNotesByod.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setReleaseNoteByodToDelete(null);
      setIsBulkDelete(false);
    }
  };

  const rejectDelete = () => {
    setReleaseNoteByodToDelete(null);
    setIsBulkDelete(false);
  };

  const deleteSelectedReleaseNotesByod = () => {
    if (selectedReleaseNotesByod.length === 0) return;
    setIsBulkDelete(true);
  };

  return (
    <ContentCard>
      <HeaderTitle title="Release Notes BYOD" />

      <TableHeader
        onCreate={handleShowCreateReleaseNoteByod}
        onDelete={deleteSelectedReleaseNotesByod}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['release-notes-byod'] });
          setIsRefreshing(false);
        }}
        visibility={toolbar}
      />

      <AppLoading isLoading={loadingReleaseNotesByod || isRefreshing}>
        <AppTableSingle
          headers={[
            { name: 'Versión', field: 'version', width: '150px' },
            { name: 'Descripción', field: 'description' },
          ]}
          data={releaseNotesByodWithId}
          onEdit={hasEditPermissions ? handleEditReleaseNoteByod : undefined}
          onDelete={hasEditPermissions ? handleDeleteReleaseNoteByod : undefined}
          defaultSortField="version"
          defaultSortOrder={-1}
        />

        <AppDialog
          visible={isModalVisible}
          title={
            newEditReleaseNoteByod.releaseNoteByodId
              ? t('parameters.releaseNotesByod.updateReleaseNote', {
                  version: newEditReleaseNoteByod.version,
                })
              : t('parameters.releaseNotesByod.createReleaseNote')
          }
          onClose={() => setIsModalVisible(false)}
          onConfirm={handleCreateEdit}
        >
          <ReleaseNoteByodForm
            values={newEditReleaseNoteByod}
            handleChange={(field, value) =>
              setNewEditReleaseNoteByod((prev) => ({ ...prev, [field]: value }))
            }
            errors={validationErrors}
          />
        </AppDialog>

        <ConfirmDeleteDialog
          confirmDelete={confirmDelete}
          rejectDelete={rejectDelete}
          visible={isBulkDelete || releaseNoteByodToDelete !== null}
          message={t('parameters.releaseNotesByod.confirmDelete', {
            version: releaseNoteByodToDelete?.version,
          })}
        />
      </AppLoading>
    </ContentCard>
  );
};

export default ReleaseNotesByod;
